import {
  ComponentProps,
  FocusEventHandler,
  FormEventHandler,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { TaskQueue } from 'record-it-utils/utils/TaskQueue/TaskQueue';
import { useEventCallback } from '../useEventCallback';
import { useIsFocused } from '../useIsFocused';

export function useFixedLengthNumberInput(
  options: UseFixedLengthNumberInputOptions
): UseFixedLengthNumberInputReturnValue {
  const [bufferedValue, setBufferedValue] = useState<string>(
    options.value !== null ? String(options.value) : ''
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    isFocused,
    handleBlur,
    handleFocus: handleIsFocusedFocus
  } = useIsFocused({ onFocus: options.onFocus, onBlur: options.onBlur });

  const handleInput: FormEventHandler<HTMLInputElement> = useEventCallback(
    (event) => {
      const { value } = event.currentTarget;
      if (value.match(/^[0-9]*$/)) {
        setBufferedValue(value);

        if (value.length >= options.valueLength) {
          options.onValueLengthReached?.();
        }
      }
    }
  );

  const handleFocus: FocusEventHandler<HTMLInputElement> = useEventCallback(
    (event) => {
      inputRef.current?.setSelectionRange(0, inputRef.current.value.length);

      handleIsFocusedFocus(event);
    }
  );

  useEffect(() => {
    setBufferedValue((oldValue) => {
      const newValue = numberToString(options.value);
      if (newValue !== oldValue) {
        TaskQueue.queueTask(() => {
          inputRef.current?.setSelectionRange(0, inputRef.current.value.length);
        });
      }

      return newValue;
    });
  }, [options.anyInputIsFocused, options.value]);

  const value = useMemo(() => {
    return isFocused || options.anyInputIsFocused
      ? bufferedValue
      : numberToString(options.value);
  }, [isFocused, options.anyInputIsFocused, options.value, bufferedValue]);

  const focus = useCallback(() => {
    assertNotNullOrUndefined(
      inputRef.current,
      "can't focus when the input is not rendered"
    );
    inputRef.current.focus();
  }, [inputRef]);

  return {
    isFocused,
    bufferedValue: stringToNumber(bufferedValue),
    focus,
    inputProps: {
      ref: inputRef,
      size: options.valueLength,
      value,
      onInput: handleInput,
      onBlur: handleBlur,
      onFocus: handleFocus
    }
  };
}

export type UseFixedLengthNumberInputOptions = {
  value: number | null;
  anyInputIsFocused: boolean;
  valueLength: number;
  onFocus: FocusEventHandler<HTMLInputElement>;
  onBlur: () => void;
  onValueLengthReached?: () => void;
};

export type UseFixedLengthNumberInputReturnValue = {
  isFocused: boolean;
  bufferedValue: number | null;
  inputProps: Omit<ComponentProps<'input'>, 'ref'> & {
    ref: MutableRefObject<HTMLInputElement | null>;
  };
  focus: () => void;
};

function stringToNumber(stringValue: string): number | null {
  const trimmedValue = stringValue.trim();
  const numberValue = trimmedValue !== '' ? Number(trimmedValue) : NaN;
  return isNaN(numberValue) ? null : numberValue;
}

function numberToString(numberValue: number | null): string {
  if (numberValue == null) {
    return '';
  }

  return StringUtils.padLeft(String(numberValue), 2);
}
